import {
  EmailTemplate,
  EmailTemplateChannel,
} from '@/lib/admin/email-templates/models/email-template.model';

/**
 * Colonne ordinabili della tabella unificata. `recipients` ordina per numero
 * totale di destinatari (email + notifica): è la lettura utile della colonna,
 * dove zero significa "template spento su entrambi i canali".
 */
export type EmailTemplateSortKey =
  | 'template_key'
  | 'description'
  | 'recipients'
  | 'updated_at'
  | 'created_at';

export interface EmailTemplateSort {
  key: EmailTemplateSortKey;
  direction: 'asc' | 'desc';
}

/** Testo del canale indicato (oggetto per l'email, messaggio per la notifica). */
export const getChannelContent = (
  template: EmailTemplate,
  channel: EmailTemplateChannel
): string =>
  (channel === 'email' ? template.subject : template.notification_message) ?? '';

/** Destinatari dei due canali in un array solo, per ricerca e conteggi. */
const allRecipients = (template: EmailTemplate): string[] => [
  ...template.recipients,
  ...template.notification_recipients,
];

/**
 * Ricerca istantanea case-insensitive su chiave, descrizione, testi di ENTRAMBI i
 * canali e destinatari: la tabella non è più divisa per canale, quindi cercare
 * "client" o una parola dell'oggetto deve trovare la riga comunque.
 */
export const filterTemplates = (
  templates: EmailTemplate[],
  search: string
): EmailTemplate[] => {
  const needle = search.trim().toLowerCase();
  if (!needle) return templates;

  return templates.filter((template) => {
    const haystack = [
      template.template_key,
      template.description ?? '',
      template.subject,
      template.notification_message ?? '',
      allRecipients(template).join(' '),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });
};

const compareStrings = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/** Le date mancanti finiscono in fondo a prescindere dalla direzione. */
const compareDates = (a: string | null, b: string | null) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
};

export const sortTemplates = (
  templates: EmailTemplate[],
  sort: EmailTemplateSort
): EmailTemplate[] => {
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...templates].sort((a, b) => {
    switch (sort.key) {
      case 'template_key':
        return factor * compareStrings(a.template_key, b.template_key);
      case 'description':
        return factor * compareStrings(a.description ?? '', b.description ?? '');
      case 'recipients':
        return factor * (allRecipients(a).length - allRecipients(b).length);
      case 'updated_at':
        return factor * compareDates(a.updated_at, b.updated_at);
      case 'created_at':
        return factor * compareDates(a.created_at, b.created_at);
      default:
        return 0;
    }
  });
};
